import React from "react";
import styles from "./ShimmerImage.module.css";

type Props = React.ImgHTMLAttributes<HTMLImageElement> & {
  fallbackSrc?: string;
};

const loadedSrcCache = new Set<string>();

const ShimmerImage: React.FC<Props> = ({ src, fallbackSrc, className, onLoad, onError, alt, ...rest }) => {
  const [currentSrc, setCurrentSrc] = React.useState(src);
  const [isLoaded, setIsLoaded] = React.useState(() => Boolean(src && loadedSrcCache.has(src)));
  const imageRef = React.useRef<HTMLImageElement | null>(null);

  React.useLayoutEffect(() => {
    setCurrentSrc(src);
    setIsLoaded(Boolean(src && loadedSrcCache.has(src)));
  }, [src]);

  React.useEffect(() => {
    const image = imageRef.current;
    if (!currentSrc || !image?.complete) return;
    loadedSrcCache.add(currentSrc);
    setIsLoaded(true);
  }, [currentSrc]);

  const handleLoad = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    if (currentSrc) {
      loadedSrcCache.add(currentSrc);
    }
    setIsLoaded(true);
    onLoad?.(event);
  };

  const handleError = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    if (fallbackSrc && currentSrc !== fallbackSrc) {
      setCurrentSrc(fallbackSrc);
      setIsLoaded(false);
    } else {
      setIsLoaded(true);
    }
    onError?.(event);
  };

  return (
    <span className={`${styles.wrapper} ${className || ""}`.trim()}>
      {!isLoaded && <span className={styles.shimmer} aria-hidden="true" />}
      <img
        ref={imageRef}
        {...rest}
        src={currentSrc}
        alt={alt ?? ""}
        className={`${className || ""} ${styles.image} ${isLoaded ? styles.loaded : styles.loading}`.trim()}
        onLoad={handleLoad}
        onError={handleError}
        loading={rest.loading || "lazy"}
        decoding={rest.decoding || "async"}
      />
    </span>
  );
};

export default ShimmerImage;
